import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

// Howyin palette
const INK = "#1A1614";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const HAIR = "#E8E1D3";
const MUTED = "#8A857F";

const Sub = ({ label, value, mono }) =>
  value ? (
    <div style={{ fontSize: 11, color: MUTED, fontFamily: mono ? "monospace" : "inherit" }}>
      <span style={{ color: "#B6AF9F" }}>{label}: </span>
      {value}
    </div>
  ) : null;

const whereBadge = (t) => {
  const map = { reel: { bg: "#EAF2FF", fg: "#2563EB" }, live: { bg: "#FDEAEA", fg: "#DC2626" } };
  const c = map[t] || { bg: "#F1EEE7", fg: MUTED };
  return (
    <span style={{ background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, textTransform: "capitalize" }}>
      {t || "-"}
    </span>
  );
};

const GiftTransferLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState("");
  const [contentType, setContentType] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const q = `giftTransaction/admin/giftAuditLogs?page=${page}&limit=${limit}` +
        (search ? `&search=${encodeURIComponent(search)}` : "") +
        (contentType ? `&contentType=${contentType}` : "");
      const res = await apiInstanceFetch.get(q);
      if (res && res.status) {
        setLogs(res.logs || []);
        setTotal(res.total || 0);
      }
    } catch (e) {
      console.error("giftAuditLogs fetch", e);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, contentType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const th = { textAlign: "left", padding: "10px 12px", fontSize: 12, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${HAIR}`, whiteSpace: "nowrap" };
  const td = { padding: "10px 12px", fontSize: 13, color: INK, borderBottom: `1px solid ${HAIR}`, verticalAlign: "top" };

  return (
    <div style={{ padding: 16 }}>
      <Title name="Gift / Transfer Logs" />

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "12px 0" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchLogs(); } }}
          placeholder="Search sender / receiver / IP / gift…"
          style={{ flex: "1 1 280px", padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM }}
        />
        <select value={contentType} onChange={(e) => { setContentType(e.target.value); setPage(1); }}
          style={{ padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM }}>
          <option value="">All places</option>
          <option value="reel">Reel</option>
          <option value="live">Live</option>
        </select>
        <button onClick={() => { setPage(1); fetchLogs(); }}
          style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: GOLD, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          Search
        </button>
        <span style={{ fontSize: 12, color: MUTED }}>{total} transfers</span>
      </div>

      <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1050 }}>
          <thead>
            <tr>
              <th style={th}>Date / Time</th>
              <th style={th}>Sender (gifter)</th>
              <th style={th}>Receiver</th>
              <th style={th}>Gift</th>
              <th style={th}>Coins</th>
              <th style={th}>Where</th>
              <th style={th}>Sender IP / Device</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={7}>Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={7}>No gift/transfer records.</td></tr>
            ) : (
              logs.map((l) => (
                <tr key={l._id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{dayjs(l.createdAt).format("DD MMM YYYY")}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{dayjs(l.createdAt).format("hh:mm:ss A")}</div>
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{l.sender.isAnonymous ? "Anonymous" : (l.sender.name || "-")}</div>
                    <Sub label="Email" value={l.sender.email} />
                    <Sub label="Phone" value={l.sender.phone} />
                    <Sub label="Platform" value={l.sender.platform} />
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{l.receiver.name || "-"}
                      <span style={{ fontSize: 10, color: MUTED, fontWeight: 500 }}> ({l.receiver.type})</span>
                    </div>
                    <Sub label="Email" value={l.receiver.email} />
                    <Sub label="Phone" value={l.receiver.phone} />
                    <Sub label="Platform" value={l.receiver.platform} />
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {l.gift.icon ? <img src={l.gift.icon} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} /> : null}
                      <span>{l.gift.name} ×{l.gift.quantity}</span>
                    </div>
                  </td>
                  <td style={{ ...td, fontWeight: 700, color: GOLD }}>{l.gift.coins}</td>
                  <td style={td}>
                    {whereBadge(l.where.contentType)}
                    <div style={{ fontSize: 10, color: MUTED, fontFamily: "monospace", marginTop: 3 }}>{l.where.contentId}</div>
                  </td>
                  <td style={td}>
                    <Sub label="IP" value={l.sender.ip || "—"} mono />
                    <Sub label="Device" value={l.sender.deviceModel || "—"} />
                    <Sub label="Device ID" value={l.sender.deviceId || "—"} mono />
                    {l.sender.userAgent ? (
                      <div title={l.sender.userAgent} style={{ fontSize: 10, color: "#B6AF9F", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {l.sender.userAgent}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 14 }}>
        <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
          style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${HAIR}`, background: page <= 1 ? "#F3F0E9" : "#fff", color: INK, cursor: page <= 1 ? "default" : "pointer", fontSize: 13 }}>
          Prev
        </button>
        <span style={{ fontSize: 13, color: MUTED }}>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${HAIR}`, background: page >= totalPages ? "#F3F0E9" : "#fff", color: INK, cursor: page >= totalPages ? "default" : "pointer", fontSize: 13 }}>
          Next
        </button>
      </div>
    </div>
  );
};

export default GiftTransferLogs;
